import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Building2,
  GraduationCap,
  Info,
  Mail,
  MailWarning,
  Phone,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  MailX,
  RotateCcw,
  Plus,
  Pencil,
  Trash2,
  Shield,
  Users,
  Flag,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { MockedChip } from "../common/MockedChip";
import { Callout } from "../../../ui/components/Callout";
import { useAuth, usePermission } from "../../lib/auth/hooks";
import { useWebSocket } from "../../hooks/useWebSocket";
import {
  hasPermissionInPayload,
  hasRoleInPayload,
} from "../../lib/auth/permissions";
import * as adminUsersApi from "../../lib/api/admin-users";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import * as userApi from "../../lib/api/users";
import type {
  Organization,
  OrganizationMember,
  OrganizationPendingInvite,
  OrganizationScopedRole,
  OrgAssignablePermission,
} from "../../lib/api/users";
import {
  approveOrgCertificationAccessRequest,
  denyOrgCertificationAccessRequest,
  fetchOrgCertificationAccessRequests,
  fetchOrgCertificationMembers,
  type OrgCertAccessRequest,
  type OrgCertEnrollRole,
  type OrgCertMember,
  type OrgCertPendingInvite,
} from "../../lib/api/certifications-org";
import {
  fetchCertificationModules,
  type CertificationModule,
} from "../../lib/api/certifications";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import * as listViewCardStyles from "../../../ui/components/ListView/components/ListViewCard/ListViewCard.style.css";
import { ListViewTechnicianCard } from "../../../ui/components/ListView/components/ListViewTechnicianCard";
import type { ListParams } from "../../../ui/api/listApi.types";
import { InviteMemberModal } from "./InviteMemberModal";
import { InviteToEnrollModal } from "./InviteToEnrollModal";
import { SendCertificationReminderModal } from "./SendCertificationReminderModal";
import { RoleFormModal } from "./RoleFormModal";
import { OrganizationAccountDangerZone } from "./OrganizationAccountDangerZone";
import { OrganizationFeatureFlagsSection } from "./OrganizationFeatureFlagsSection";
import { ListViewOrgMemberCard } from "../../../ui/components/ListView/components/ListViewOrgMemberCard";
import { shortDate } from "../../../ui/components/ListView/components/DeckCard/logic/formatters";
import { buildMembershipColumns } from "./buildMembershipColumns";
import { toMembershipRows, type MembershipRow } from "./membershipListTypes";
import { useI18n } from "../../lib/i18n";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import type { ContextAction } from "../../../ui/components/ContextActions";
import * as fieldStyles from "./OrganizationFormFields.css";
import * as layout from "./OrganizationEditPage.css";
import * as styles from "./OrganizationDetailPage.css";

const SECTION_IDS = {
  contact: "org-contact",
  membership: "org-membership",
  certifications: "org-certifications",
  roles: "org-roles",
  featureOverrides: "org-feature-overrides",
  security: "org-security",
} as const;

const ENROLL_ROLES_TOOLTIP =
  "Only members with one of these roles can enroll in certifications. Users not listed need an enroll-capable role (or a certification access approval).";

type CertListRow =
  | {
      kind: "member";
      id: string;
      member: OrgCertMember;
      displayName: string;
      email: string;
    }
  | {
      kind: "access_request";
      id: string;
      request: OrgCertAccessRequest;
      displayName: string;
      email: string;
    }
  | {
      kind: "invited";
      id: string;
      invite: OrgCertPendingInvite;
      displayName: string;
      email: string;
    };

function toCertTechnicianCard(member: OrgCertMember) {
  return {
    userId: member.userId,
    name: member.name || member.email,
    email: member.email,
    avatarUrl: member.avatarUrl,
    modules: member.modules.map((m) => ({
      moduleId: m.moduleId,
      moduleTitle: m.title,
      progressPercent: m.progressPercent,
      status: m.status,
      completedAt: m.completedAt,
      expiresAt: m.expiresAt,
      trophyImageUrl: m.trophyImageUrl,
    })),
  };
}

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function roleDisplayLabel(role: OrganizationScopedRole): string {
  return role.label?.trim() || formatRoleLabel(role.name);
}

function isAdminSystemRole(role: {
  systemKey?: string | null;
  name?: string | null;
}): boolean {
  return (
    role.systemKey === "admin" ||
    role.name === "admin" ||
    role.name === "pro:admin"
  );
}

function formatCreatedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatAddressLines(
  org: Organization,
): { line1: string; line2: string } | null {
  const streetParts = [org.address, org.addressSecondary]
    .filter((part) => part?.trim())
    .map((part) => part!.trim());
  const line1 = streetParts.join(", ");

  const city = org.city?.trim();
  const state = org.state?.trim();
  const zip = org.zip?.trim();
  const regionZip = [state, zip].filter(Boolean).join(" ");
  const line2 = [city, regionZip].filter(Boolean).join(city && regionZip ? ", " : "");

  if (!line1 && !line2) return null;
  return { line1: line1 || line2, line2: line1 ? line2 : "" };
}

const SOLE_ADMIN_ROLE_TOOLTIP =
  "This user cannot change roles until at least one other member has the administrator organization role.";

function countActiveOrgAdmins(
  members: OrganizationMember[],
  orgRoles: OrganizationScopedRole[],
): number {
  const adminRoleIds = new Set(
    orgRoles.filter((r) => isAdminSystemRole(r)).map((r) => r.id),
  );
  return members.filter(
    (m) =>
      m.membershipStatus === "active" &&
      (m.roleSystemKey === "admin" ||
        adminRoleIds.has(m.roleId ?? "") ||
        isAdminSystemRole({ name: m.roleName, systemKey: m.roleSystemKey })),
  ).length;
}

function isSoleOrgAdmin(
  member: OrganizationMember,
  activeAdminCount: number,
  orgRoles: OrganizationScopedRole[],
): boolean {
  const adminRole = orgRoles.find((r) => isAdminSystemRole(r));
  const isAdmin =
    member.roleSystemKey === "admin" ||
    (adminRole && member.roleId === adminRole.id) ||
    isAdminSystemRole({ name: member.roleName, systemKey: member.roleSystemKey });
  return (
    member.membershipStatus === "active" &&
    Boolean(isAdmin) &&
    activeAdminCount <= 1
  );
}

type MemberConfirmAction =
  | { type: "approve"; member: OrganizationMember }
  | { type: "deny"; member: OrganizationMember }
  | { type: "role"; member: OrganizationMember; newRoleId: string }
  | { type: "remove"; members: OrganizationMember[] }
  | { type: "restore"; member: OrganizationMember }
  | { type: "cancelInvite"; invite: OrganizationPendingInvite };

function memberDisplayName(member: OrganizationMember): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ").trim();
  return name || member.email;
}

function sortMembersForDisplay(
  members: OrganizationMember[],
  isOrgAdmin: boolean,
): OrganizationMember[] {
  if (!isOrgAdmin) return members;
  const pending = members.filter((m) => m.membershipStatus === "pending");
  const active = members.filter((m) => m.membershipStatus === "active");
  return [...pending, ...active];
}

export function OrganizationDetailPage({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken, user, userPayload } = useAuth();
  const { subscribe, unsubscribe } = useWebSocket();
  const { hasPermissionSync: canUpdateOrg } = usePermission("organization:update");
  const { hasPermissionSync: canDeleteOrg } = usePermission("organization:delete");
  const token = accessToken ?? localStorage.getItem("access_token");
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("org-detail");

  const isSuperAdmin = hasRoleInPayload(userPayload, "super-admin");

  const reloadOrg = React.useCallback(async () => {
    if (!token || !orgId) return;
    const data = await userApi.fetchOrganization(token, orgId, {
      includeDeleted: isSuperAdmin,
    });
    setOrg(data);
  }, [token, orgId, isSuperAdmin]);

  const [org, setOrg] = React.useState<Organization | null>(null);
  const [members, setMembers] = React.useState<OrganizationMember[]>([]);
  const [pendingInvites, setPendingInvites] = React.useState<
    OrganizationPendingInvite[]
  >([]);
  const [loading, setLoading] = React.useState(true);
  const [membersLoading, setMembersLoading] = React.useState(true);
  const [invitesLoading, setInvitesLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [membersError, setMembersError] = React.useState<string | null>(null);
  const [orgRoles, setOrgRoles] = React.useState<OrganizationScopedRole[]>([]);
  const [memberActionLoading, setMemberActionLoading] = React.useState(false);
  const [memberRefreshKey, setMemberRefreshKey] = React.useState(0);
  const [confirmAction, setConfirmAction] =
    React.useState<MemberConfirmAction | null>(null);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const [inviteEnrollModalOpen, setInviteEnrollModalOpen] = React.useState(false);
  const [reminderModalOpen, setReminderModalOpen] = React.useState(false);
  const [reminderTargets, setReminderTargets] = React.useState<OrgCertMember[]>(
    [],
  );
  const [orgRolesLoading, setOrgRolesLoading] = React.useState(false);
  const [roleModalOpen, setRoleModalOpen] = React.useState(false);
  const [editingRole, setEditingRole] = React.useState<
    OrganizationScopedRole | undefined
  >(undefined);
  const [availablePermissions, setAvailablePermissions] = React.useState<
    OrgAssignablePermission[]
  >([]);
  const [roleActionLoading, setRoleActionLoading] = React.useState(false);
  const [certMembers, setCertMembers] = React.useState<OrgCertMember[]>([]);
  const [enrollRoles, setEnrollRoles] = React.useState<OrgCertEnrollRole[]>([]);
  const [certPendingInvites, setCertPendingInvites] = React.useState<
    OrgCertPendingInvite[]
  >([]);
  const [certAccessRequests, setCertAccessRequests] = React.useState<
    OrgCertAccessRequest[]
  >([]);
  const [certLoading, setCertLoading] = React.useState(false);
  const [certError, setCertError] = React.useState<string | null>(null);
  const [certRefreshKey, setCertRefreshKey] = React.useState(0);
  const [certActionLoading, setCertActionLoading] = React.useState(false);
  const [certCatalogModules, setCertCatalogModules] = React.useState<
    CertificationModule[]
  >([]);

  const certCatalogForCards = React.useMemo(
    () =>
      certCatalogModules.map((m) => ({
        id: m.id,
        title: m.title,
        trophyImageUrl: m.trophyImageUrl,
        heroImageUrl: m.heroImageUrl,
        description: m.description,
        sortOrder: m.sortOrder,
        isActive: m.isActive,
      })),
    [certCatalogModules],
  );

  const reloadMembers = React.useCallback(async () => {
    if (!token || !orgId) return;
    setMembersError(null);
    const list = await userApi.fetchOrganizationMembers(token, orgId);
    setMembers(list);
    setMemberRefreshKey((k) => k + 1);
  }, [token, orgId]);

  React.useEffect(() => {
    if (!token || !orgId) return;
    setLoading(true);
    userApi
      .fetchOrganization(token, orgId, { includeDeleted: isSuperAdmin })
      .then((data) => setOrg(data))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load organization"))
      .finally(() => setLoading(false));
  }, [token, orgId, isSuperAdmin]);

  React.useEffect(() => {
    if (!token || !orgId) return;
    setMembersLoading(true);
    setMembersError(null);
    userApi
      .fetchOrganizationMembers(token, orgId)
      .then(setMembers)
      .catch((e) =>
        setMembersError(
          e instanceof Error ? e.message : "Failed to load members",
        ),
      )
      .finally(() => setMembersLoading(false));
  }, [token, orgId]);

  const membership = user?.organizations?.find((o) => o.id === orgId);
  const isOrgAdmin =
    isSuperAdmin ||
    membership?.roleSystemKey === "admin" ||
    membership?.roleName === "admin" ||
    membership?.roleName === "pro:admin" ||
    userPayload?.orgs?.some(
      (o) => o.id === orgId && o.systemKey === "admin",
    ) === true;
  const canManageRoles = isOrgAdmin;
  const activeOrgMatches = userPayload?.org_id === orgId;
  const canViewCertifications =
    isOrgAdmin ||
    (activeOrgMatches &&
      (hasPermissionInPayload(userPayload, "certification:view") ||
        hasPermissionInPayload(userPayload, "certification:manage")));
  const canManageCertifications =
    isOrgAdmin ||
    (activeOrgMatches &&
      hasPermissionInPayload(userPayload, "certification:manage"));
  const isDisabledOrg = Boolean(org?.deletedAt);
  const orgDisabledSnapshot = {
    deletedAt: org?.deletedAt,
    disabledByUserId: org?.disabledByUserId,
    disabledByName: org?.disabledByName,
    disabledByEmail: org?.disabledByEmail,
  };

  const reloadPendingInvites = React.useCallback(async () => {
    if (!token || !orgId || !isOrgAdmin) {
      setPendingInvites([]);
      return;
    }
    try {
      setPendingInvites(
        await userApi.fetchOrganizationPendingInvites(token, orgId),
      );
      setMemberRefreshKey((k) => k + 1);
    } catch {
      setPendingInvites([]);
    }
  }, [token, orgId, isOrgAdmin]);

  React.useEffect(() => {
    if (!token || !orgId || !isOrgAdmin) {
      setPendingInvites([]);
      setInvitesLoading(false);
      return;
    }
    setInvitesLoading(true);
    void reloadPendingInvites().finally(() => setInvitesLoading(false));
  }, [token, orgId, isOrgAdmin, reloadPendingInvites]);

  React.useEffect(() => {
    if (!token || !orgId || !(isOrgAdmin || canManageCertifications)) {
      if (!isOrgAdmin) {
        setOrgRoles([]);
        setAvailablePermissions([]);
      }
      setOrgRolesLoading(false);
      return;
    }
    setOrgRolesLoading(true);
    const rolesPromise = userApi.fetchOrganizationRoles(token, orgId);
    const permsPromise = isOrgAdmin
      ? userApi.fetchOrganizationAvailablePermissions(token, orgId)
      : Promise.resolve([] as OrgAssignablePermission[]);
    Promise.all([rolesPromise, permsPromise])
      .then(([roles, perms]) => {
        setOrgRoles(roles);
        if (isOrgAdmin) setAvailablePermissions(perms);
      })
      .catch(() => {
        if (!isOrgAdmin) setOrgRoles([]);
        if (isOrgAdmin) setAvailablePermissions([]);
      })
      .finally(() => setOrgRolesLoading(false));
  }, [token, orgId, isOrgAdmin, canManageCertifications]);

  const reloadCertSection = React.useCallback(async () => {
    if (!token || !orgId || !canViewCertifications) {
      setCertMembers([]);
      setEnrollRoles([]);
      setCertPendingInvites([]);
      setCertAccessRequests([]);
      setCertCatalogModules([]);
      return [] as OrgCertMember[];
    }
    setCertError(null);
    const [membersResult, requests, catalog] = await Promise.all([
      fetchOrgCertificationMembers(token, orgId),
      fetchOrgCertificationAccessRequests(token, orgId).catch(
        () => [] as OrgCertAccessRequest[],
      ),
      fetchCertificationModules(token).catch(() => [] as CertificationModule[]),
    ]);
    setCertMembers(membersResult.members);
    setEnrollRoles(membersResult.enrollRoles);
    setCertPendingInvites(membersResult.pendingInvites);
    setCertAccessRequests(requests);
    setCertCatalogModules(catalog);
    setCertRefreshKey((k) => k + 1);
    return membersResult.members;
  }, [token, orgId, canViewCertifications]);

  const [certForcedFlashIds, setCertForcedFlashIds] = React.useState(
    () => new Map<string, number>(),
  );
  const pendingCertFlashIdsRef = React.useRef<Set<string>>(new Set());
  const certMembersRef = React.useRef(certMembers);
  certMembersRef.current = certMembers;

  const bumpCertForcedFlashes = React.useCallback((ids: string[]) => {
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return;
    setCertForcedFlashIds((prev) => {
      const next = new Map(prev);
      for (const id of unique) next.set(id, (next.get(id) ?? 0) + 1);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!token || !orgId || !canViewCertifications) {
      setCertMembers([]);
      setEnrollRoles([]);
      setCertPendingInvites([]);
      setCertAccessRequests([]);
      setCertCatalogModules([]);
      setCertLoading(false);
      return;
    }
    setCertLoading(true);
    void reloadCertSection()
      .catch((e) =>
        setCertError(
          e instanceof Error ? e.message : "Failed to load certifications",
        ),
      )
      .finally(() => setCertLoading(false));
  }, [token, orgId, canViewCertifications, reloadCertSection]);

  React.useEffect(() => {
    if (!orgId || !canViewCertifications) return;
    const topic = `entity:certifications:org:${orgId}`;
    const handler = (msg: { payload?: unknown }) => {
      const payload = msg.payload as { userId?: string } | undefined;
      if (typeof payload?.userId === "string" && payload.userId) {
        pendingCertFlashIdsRef.current.add(payload.userId);
      }
      const beforeByUser = new Map(
        certMembersRef.current.map((m) => [
          m.userId,
          JSON.stringify(m.modules ?? []),
        ]),
      );
      void reloadCertSection()
        .then((nextMembers) => {
          const changedIds: string[] = [];
          for (const member of nextMembers) {
            const prev = beforeByUser.get(member.userId);
            const next = JSON.stringify(member.modules ?? []);
            if (prev == null || prev !== next) changedIds.push(member.userId);
          }
          for (const id of pendingCertFlashIdsRef.current) changedIds.push(id);
          pendingCertFlashIdsRef.current.clear();
          bumpCertForcedFlashes(changedIds);
        })
        .catch(() => {
          pendingCertFlashIdsRef.current.clear();
        });
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [
    orgId,
    canViewCertifications,
    subscribe,
    unsubscribe,
    reloadCertSection,
    bumpCertForcedFlashes,
  ]);

  const openCreateRoleModal = () => {
    setEditingRole(undefined);
    setRoleModalOpen(true);
  };

  const openEditRoleModal = (role: OrganizationScopedRole) => {
    setEditingRole(role);
    setRoleModalOpen(true);
  };

  const handleDeleteRoles = async (roleIds: string[]) => {
    if (!token || roleIds.length === 0) return;
    const deletable = orgRoles.filter(
      (r) =>
        roleIds.includes(r.id) &&
        !isAdminSystemRole(r) &&
        r.systemKey !== "member",
    );
    if (deletable.length === 0) return;

    const confirmed = window.confirm(
      deletable.length === 1
        ? `Delete role "${roleDisplayLabel(deletable[0]!)}"? Members with this role will be reassigned to Member.`
        : `Delete ${deletable.length} roles? Members with these roles will be reassigned to Member.`,
    );
    if (!confirmed) return;

    setRoleActionLoading(true);
    try {
      const deletedIds: string[] = [];
      for (const role of deletable) {
        await userApi.deleteOrganizationRole(token, orgId, role.id);
        deletedIds.push(role.id);
      }
      setOrgRoles((prev) => prev.filter((r) => !deletedIds.includes(r.id)));
    } catch (e) {
      setMembersError(
        e instanceof Error ? e.message : "Failed to delete role",
      );
    } finally {
      setRoleActionLoading(false);
    }
  };

  const fetchRolesList = React.useCallback(
    (params: ListParams) => {
      const start = (params.page - 1) * params.limit;
      const q = params.search?.toLowerCase() ?? "";
      const filtered = q
        ? orgRoles.filter((r) =>
            [r.label, r.name, r.description]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(q)),
          )
        : orgRoles;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [orgRoles],
  );

  const getRoleContextActions = React.useCallback(
    (row: OrganizationScopedRole): ListViewContextAction[] => {
      const actions: ListViewContextAction[] = [
        {
          id: "edit",
          label: isAdminSystemRole(row) ? "View" : "Edit",
          icon: <Pencil size={14} />,
          bulk: false,
          onClick: (ids) => {
            const target =
              orgRoles.find((r) => r.id === ids[0]) ??
              (ids[0] === row.id ? row : undefined);
            if (target) openEditRoleModal(target);
          },
        },
      ];
      if (!isAdminSystemRole(row) && row.systemKey !== "member") {
        actions.push({
          id: "remove",
          label: "Delete",
          icon: <Trash2 size={14} />,
          danger: true,
          disabled: roleActionLoading,
          onClick: (ids) => void handleDeleteRoles(ids),
        });
      }
      return actions;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers use stable orgId/token
    [token, orgId, orgRoles, roleActionLoading],
  );

  const displayedMembers = React.useMemo(
    () => sortMembersForDisplay(members, isOrgAdmin),
    [members, isOrgAdmin],
  );

  const roleOptions = React.useMemo(
    () =>
      orgRoles.map((role) => ({
        id: role.id,
        name: role.label?.trim() || role.name,
        color: role.color,
      })),
    [orgRoles],
  );

  const activeAdminCount = React.useMemo(
    () => countActiveOrgAdmins(members, orgRoles),
    [members, orgRoles],
  );

  const isSoleAdminById = React.useCallback(
    (rowId: string) => {
      const member = members.find((m) => m.userId === rowId);
      return member ? isSoleOrgAdmin(member, activeAdminCount, orgRoles) : false;
    },
    [members, activeAdminCount, orgRoles],
  );

  const fetchMembershipRows = React.useCallback(
    async (params: ListParams) => {
      let rows = toMembershipRows(members, pendingInvites, isOrgAdmin);
      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            r.displayName.toLowerCase().includes(q) ||
            r.email.toLowerCase().includes(q) ||
            (r.roleName?.toLowerCase().includes(q) ?? false),
        );
      }
      return { data: rows, total: rows.length };
    },
    [members, pendingInvites, isOrgAdmin, memberRefreshKey],
  );

  const enrollRoleIds = React.useMemo(
    () => new Set(enrollRoles.map((r) => r.id)),
    [enrollRoles],
  );

  const toReminderTarget = React.useCallback(
    (member: OrganizationMember): OrgCertMember => {
      const fromCert = certMembers.find((m) => m.userId === member.userId);
      if (fromCert) return fromCert;
      return {
        userId: member.userId,
        firstName: member.firstName ?? "",
        lastName: member.lastName ?? "",
        name:
          [member.firstName, member.lastName].filter(Boolean).join(" ").trim() ||
          member.email,
        email: member.email,
        avatarUrl: member.avatarUrl ?? null,
        role: member.roleId
          ? {
              id: member.roleId,
              label: member.roleLabel ?? member.roleName ?? null,
              color: member.roleColor ?? null,
            }
          : null,
        modules: [],
      };
    },
    [certMembers],
  );

  const openReminderModal = React.useCallback((targets: OrgCertMember[]) => {
    if (targets.length === 0) return;
    setReminderTargets(targets);
    setReminderModalOpen(true);
  }, []);

  const buildMemberMenuActions = React.useCallback(
    (row: MembershipRow, member: OrganizationMember | undefined): ListViewContextAction[] => [
      ...(isSuperAdmin && row.deletedAt && member
        ? [{ id: "restore", label: "Restore user account", icon: <RotateCcw size={14} />, onClick: () => setConfirmAction({ type: "restore", member }) }]
        : []),
      ...(isOrgAdmin && row.kind === "pending" && member
        ? [
            { id: "approve", label: "Approve", icon: <UserCheck size={14} />, onClick: () => setConfirmAction({ type: "approve", member }) },
            { id: "deny", label: "Deny", danger: true as const, icon: <UserX size={14} />, onClick: () => setConfirmAction({ type: "deny", member }) },
          ]
        : []),
      ...(canManageCertifications &&
      !isDisabledOrg &&
      row.kind === "active" &&
      member &&
      !member.deletedAt &&
      member.roleId &&
      enrollRoleIds.has(member.roleId)
        ? [
            {
              id: "send-cert-reminder",
              label: "Send certification reminder",
              icon: <MailWarning size={14} />,
              onClick: (ids: string[]) => {
                const targets = ids
                  .map((id) => members.find((m) => m.userId === id))
                  .filter(
                    (m): m is OrganizationMember =>
                      m != null &&
                      m.membershipStatus === "active" &&
                      !m.deletedAt &&
                      Boolean(m.roleId) &&
                      enrollRoleIds.has(m.roleId!),
                  )
                  .map(toReminderTarget);
                openReminderModal(targets);
              },
            },
          ]
        : []),
      ...(isOrgAdmin && row.kind === "active" && member
        ? [{
            id: "remove",
            label: "Remove membership",
            danger: true as const,
            icon: <UserMinus size={14} />,
            disabled: (row.id === user?.id || isSoleAdminById(row.id)) || undefined,
            disabledTooltip: isSoleAdminById(row.id)
              ? "Cannot remove the sole administrator"
              : row.id === user?.id
              ? "You cannot remove your own membership"
              : undefined,
            onClick: (ids: string[]) => {
              const removableMembers = ids
                .map((id) => members.find((m) => m.userId === id))
                .filter((m): m is OrganizationMember => m != null);
              if (removableMembers.length > 0) setConfirmAction({ type: "remove", members: removableMembers });
            },
          }]
        : []),
      ...(isOrgAdmin && row.kind === "invited"
        ? (() => {
            const inv = pendingInvites.find((x) => x.inviteId === row.id);
            return inv ? [{ id: "cancel", label: "Cancel invitation", danger: true as const, icon: <MailX size={14} />, onClick: () => setConfirmAction({ type: "cancelInvite", invite: inv }) }] : [];
          })()
        : []),
    ],
    [
      isSuperAdmin,
      isOrgAdmin,
      canManageCertifications,
      isDisabledOrg,
      enrollRoleIds,
      toReminderTarget,
      openReminderModal,
      user?.id,
      isSoleAdminById,
      pendingInvites,
      members,
    ],
  );

  const certListRows = React.useMemo((): CertListRow[] => {
    const requests: CertListRow[] = certAccessRequests.map((request) => {
      const displayName =
        request.user?.name?.trim() ||
        [request.user?.firstName, request.user?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        request.user?.email ||
        "Unknown user";
      return {
        kind: "access_request" as const,
        id: `access-${request.userId}`,
        request,
        displayName,
        email: request.user?.email ?? "",
      };
    });
    const invites: CertListRow[] = certPendingInvites.map((invite) => ({
      kind: "invited" as const,
      id: invite.inviteId,
      invite,
      displayName: invite.email,
      email: invite.email,
    }));
    const members: CertListRow[] = certMembers.map((member) => ({
      kind: "member" as const,
      id: member.userId,
      member,
      displayName: member.name?.trim() || member.email,
      email: member.email,
    }));
    return [...requests, ...invites, ...members];
  }, [certAccessRequests, certPendingInvites, certMembers]);

  const fetchCertRows = React.useCallback(
    async (params: ListParams) => {
      let rows = certListRows;
      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            r.displayName.toLowerCase().includes(q) ||
            r.email.toLowerCase().includes(q),
        );
      }
      return { data: rows, total: rows.length };
    },
    [certListRows, certRefreshKey],
  );

  const handleCertAccessAction = React.useCallback(
    async (type: "approve" | "deny", userId: string) => {
      if (!token || !orgId) return;
      setCertActionLoading(true);
      setCertError(null);
      try {
        if (type === "approve") {
          await approveOrgCertificationAccessRequest(token, orgId, userId);
        } else {
          await denyOrgCertificationAccessRequest(token, orgId, userId);
        }
        await reloadCertSection();
        await reloadMembers();
      } catch (e) {
        setCertError(
          e instanceof Error
            ? e.message
            : "Failed to update certification access request",
        );
      } finally {
        setCertActionLoading(false);
      }
    },
    [token, orgId, reloadCertSection, reloadMembers],
  );

  const getCertContextActions = React.useCallback(
    (row: CertListRow): ListViewContextAction[] => {
      if (!canManageCertifications || isDisabledOrg) return [];
      if (row.kind === "invited") {
        return [
          {
            id: "cancel",
            label: "Cancel invitation",
            danger: true,
            icon: <MailX size={14} />,
            onClick: () =>
              setConfirmAction({
                type: "cancelInvite",
                invite: {
                  inviteId: row.invite.inviteId,
                  email: row.invite.email,
                  roleId: row.invite.roleId,
                  roleName: row.invite.roleLabel,
                  roleColor: row.invite.roleColor,
                  expiresAt: row.invite.expiresAt,
                  createdAt: row.invite.createdAt,
                },
              }),
          },
        ];
      }
      if (row.kind !== "member") return [];
      return [
        {
          id: "send-reminder",
          label: "Send reminder",
          icon: <MailWarning size={14} />,
          onClick: (ids) => {
            const targets = ids
              .map((id) => certMembers.find((m) => m.userId === id))
              .filter((m): m is OrgCertMember => m != null);
            openReminderModal(targets);
          },
        },
      ];
    },
    [
      canManageCertifications,
      isDisabledOrg,
      certMembers,
      openReminderModal,
    ],
  );

  const membershipColumns = React.useMemo(
    () =>
      buildMembershipColumns({
        isOrgAdmin,
        isSuperAdmin,
        currentUserId: user?.id,
        roleOptions,
        actionLoading: memberActionLoading,
        isSoleAdmin: isSoleAdminById,
        onApprove: (rowId) => {
          const m = members.find((x) => x.userId === rowId);
          if (m) setConfirmAction({ type: "approve", member: m });
        },
        onDeny: (rowId) => {
          const m = members.find((x) => x.userId === rowId);
          if (m) setConfirmAction({ type: "deny", member: m });
        },
        onRoleChange: (rowId, newRoleId) => {
          const m = members.find((x) => x.userId === rowId);
          if (m) setConfirmAction({ type: "role", member: m, newRoleId });
        },
        onRemove: (rowId) => {
          const m = members.find((x) => x.userId === rowId);
          if (m) setConfirmAction({ type: "remove", members: [m] });
        },
        onCancelInvite: (rowId) => {
          const inv = pendingInvites.find((x) => x.inviteId === rowId);
          if (inv) setConfirmAction({ type: "cancelInvite", invite: inv });
        },
        onRestore: (rowId) => {
          const m = members.find((x) => x.userId === rowId);
          if (m) setConfirmAction({ type: "restore", member: m });
        },
        getContextActions: (row) => buildMemberMenuActions(row, members.find((m) => m.userId === row.id)),
      }),
    [
      isOrgAdmin,
      isSuperAdmin,
      user?.id,
      roleOptions,
      memberActionLoading,
      isSoleAdminById,
      members,
      pendingInvites,
      buildMemberMenuActions,
    ],
  );

  const handleConfirmAction = React.useCallback(async () => {
    if (!token || !orgId || !confirmAction) return;
    setMemberActionLoading(true);
    setConfirmError(null);
    const actionType = confirmAction.type;
    try {
      if (confirmAction.type === "cancelInvite") {
        await userApi.cancelOrganizationInvite(
          token,
          orgId,
          confirmAction.invite.inviteId,
        );
      } else if (confirmAction.type === "approve") {
        const { member } = confirmAction;
        await userApi.approveOrganizationMembershipRequest(
          token,
          orgId,
          confirmAction.member.userId,
        );
      } else if (confirmAction.type === "deny") {
        const { member } = confirmAction;
        await userApi.denyOrganizationMembershipRequest(
          token,
          orgId,
          member.userId,
        );
      } else if (confirmAction.type === "remove") {
        await Promise.all(
          confirmAction.members.map((m) => userApi.removeOrganizationMember(token, orgId, m.userId)),
        );
      } else if (confirmAction.type === "restore") {
        await adminUsersApi.restoreUser(token, confirmAction.member.userId);
      } else if (confirmAction.type === "role") {
        const { member } = confirmAction;
        if (isSoleOrgAdmin(member, activeAdminCount, orgRoles)) {
          setConfirmError(SOLE_ADMIN_ROLE_TOOLTIP);
          return;
        }
        await userApi.updateOrganizationMemberRole(
          token,
          orgId,
          member.userId,
          confirmAction.newRoleId,
        );
      }
      setConfirmAction(null);
      await reloadMembers();
      if (actionType === "cancelInvite") {
        await reloadPendingInvites();
        if (canViewCertifications) {
          await reloadCertSection().catch(() => undefined);
        }
      }
    } catch (e) {
      setConfirmError(
        e instanceof Error ? e.message : "Something went wrong. Try again.",
      );
    } finally {
      setMemberActionLoading(false);
    }
  }, [
    token,
    orgId,
    confirmAction,
    reloadMembers,
    reloadPendingInvites,
    reloadCertSection,
    canViewCertifications,
    activeAdminCount,
  ]);

  const confirmCopy = React.useMemo(() => {
    if (!confirmAction) return null;
    if (confirmAction.type === "cancelInvite") {
      return {
        title: "Cancel invitation?",
        body: `Cancel the invitation sent to ${confirmAction.invite.email}? They will no longer be able to use that invite link.`,
        confirmLabel: "Cancel invitation",
        confirmColor: "danger" as const,
      };
    }
    if (confirmAction.type === "remove") {
      const { members } = confirmAction;
      const orgName = org?.name ?? "this organization";
      const title = members.length === 1 ? "Remove membership?" : "Remove memberships?";
      const body = members.length === 1
        ? `If you confirm, ${memberDisplayName(members[0])} will be removed and will no longer be a member of ${orgName}.`
        : `If you confirm, ${members.length} members will be removed and will no longer be members of ${orgName}.`;
      return {
        title,
        body,
        confirmLabel: members.length === 1 ? "Remove membership" : `Remove ${members.length} memberships`,
        confirmColor: "danger" as const,
      };
    }
    const name = memberDisplayName(confirmAction.member);
    if (confirmAction.type === "approve") {
      return {
        title: "Approve membership?",
        body: `Add ${name} to ${org?.name ?? "this organization"} as a member?`,
        confirmLabel: "Approve",
        confirmColor: "primary" as const,
      };
    }
    if (confirmAction.type === "restore") {
      return {
        title: "Restore user account?",
        body: `Restore ${name}? They will be able to sign in again after setting a new password via the reclaim email that is sent.`,
        confirmLabel: "Restore account",
        confirmColor: "primary" as const,
      };
    }
    if (confirmAction.type === "deny") {
      return {
        title: "Deny membership request?",
        body: `Remove ${name}'s pending request to join ${org?.name ?? "this organization"}?`,
        confirmLabel: "Deny",
        confirmColor: "danger" as const,
      };
    }
    const newRole = orgRoles.find((r) => r.id === confirmAction.newRoleId);
    const roleLabel = newRole ? formatRoleLabel(newRole.name) : "the selected role";
    return {
      title: "Change member role?",
      body: `Assign ${name} the ${roleLabel} role in ${org?.name ?? "this organization"}?`,
      confirmLabel: "Change role",
      confirmColor: "primary" as const,
    };
  }, [confirmAction, org?.name, orgRoles]);

  const addressLines = org ? formatAddressLines(org) : null;
  const contactEmail = org?.email?.trim() || null;
  const contactPhone = org?.phone?.trim() || null;
  const hasContactContent = Boolean(addressLines || contactEmail || contactPhone);

  if (loading) {
    return (
      <div style={{ padding: "32px 24px", height: 240, boxSizing: "border-box" }}>
        <AuthTransitionScreen message="Loading…" variant="fill" />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div style={{ padding: "32px 24px" }}>
        <Callout variant="error">{error ?? "Organization not found"}</Callout>
      </div>
    );
  }

  const headerBadges: React.ReactNode[] = [
    ...(org.isMocked ? [
      <MockedChip
        key="mocked"
        title={t("pro.orgs.mockDataBanner")}
      />,
    ] : []),
    ...(isDisabledOrg
      ? [
          <span
            key="disabled"
            className={styles.headerDisabledBadge}
            title={t("pro.orgs.disabledBanner")}
          >
            Organization disabled
          </span>,
        ]
      : []),
    ...(membership?.roleName
      ? [
          <span
            key="role"
            className={styles.headerChip}
            title={`Your role in this organization: ${formatRoleLabel(membership.roleName)}`}
            style={
              membership.roleColor
                ? {
                    backgroundColor: `${membership.roleColor}18`,
                    color: membership.roleColor,
                  }
                : undefined
            }
          >
            {formatRoleLabel(membership.roleName)}
          </span>,
        ]
      : []),
  ];

  const contextActions: ContextAction[] | undefined =
    canUpdateOrg() && !isDisabledOrg
      ? [
          {
            id: "edit",
            label: "Edit",
            icon: <Pencil size={16} />,
            color: "primary",
            onClick: () =>
              navigate({
                to: "/workspace/organizations/$orgId/edit",
                params: { orgId },
              }),
          },
        ]
      : undefined;

  const sectionLayout = bindSectionLayout([
    ...(hasContactContent
      ? [
          {
            id: SECTION_IDS.contact,
            label: t("pro.orgs.contact"),
            icon: <Mail size={14} />,
          },
        ]
      : []),
    {
      id: SECTION_IDS.membership,
      label: t("pro.orgs.membership"),
      icon: <Users size={14} />,
    },
    ...(canViewCertifications
      ? [
          {
            id: SECTION_IDS.certifications,
            label: "Certifications",
            icon: <GraduationCap size={14} />,
          },
        ]
      : []),
    ...(canManageRoles
      ? [
          {
            id: SECTION_IDS.roles,
            label: t("pro.orgs.memberRoles"),
            icon: <Shield size={14} />,
          },
        ]
      : []),
    ...(isOrgAdmin
      ? [
          {
            id: SECTION_IDS.featureOverrides,
            label: t("pro.users.featureOverrides"),
            icon: <Flag size={14} />,
          },
        ]
      : []),
    ...(canDeleteOrg()
      ? [
          {
            id: SECTION_IDS.security,
            label: isDisabledOrg ? t("pro.users.accountStatus") : t("pro.users.security"),
            icon: <Shield size={14} />,
          },
        ]
      : []),
  ]);

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: "All organizations",
          onClick: () => navigate({ to: "/workspace/organizations" }),
        }}
        header={{
          media: org.logoUrl ? (
            <div className={styles.headerLogo}>
              <img src={org.logoUrl} alt="" className={styles.headerLogoImg} />
            </div>
          ) : (
            <div className={styles.headerLogo}>
              <Building2 size={28} color="#9CA3AF" aria-hidden />
            </div>
          ),
          title: org.name,
          subtitle: org.description || undefined,
          badges: headerBadges,
        }}
        contextActions={contextActions}
        {...sectionLayout}
        footer={
          org.createdAt ? `Created ${formatCreatedDate(org.createdAt)}` : undefined
        }
      >
        {hasContactContent && (
          <CollapsibleSection
            id={SECTION_IDS.contact}
            title={t("pro.orgs.contact")}
            icon={<Mail size={20} />}
            expanded={isExpanded(SECTION_IDS.contact)}
            onToggleExpanded={() => toggle(SECTION_IDS.contact)}
          >
            <div className={layout.cardInner}>
              <div className={styles.contactBlock}>
                {addressLines && (
                  <div className={styles.addressBlock}>
                    <p className={styles.addressLinePrimary}>
                      {addressLines.line1}
                    </p>
                    {addressLines.line2 ? (
                      <p className={styles.addressLineSecondary}>
                        {addressLines.line2}
                      </p>
                    ) : null}
                  </div>
                )}
                {(contactEmail || contactPhone) && (
                  <div className={styles.contactChips}>
                    {contactEmail && (
                      <a
                        href={`mailto:${contactEmail}`}
                        className={styles.contactChip}
                      >
                        <span className={styles.contactChipIcon}>
                          <Mail size={14} />
                        </span>
                        {contactEmail}
                      </a>
                    )}
                    {contactPhone && (
                      <a
                        href={`tel:${contactPhone}`}
                        className={styles.contactChip}
                      >
                        <span className={styles.contactChipIcon}>
                          <Phone size={14} />
                        </span>
                        {contactPhone}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CollapsibleSection>
        )}

        <CollapsibleSection
          id={SECTION_IDS.membership}
          title={t("pro.orgs.membership")}
          subtitle={`${members.length} member${members.length !== 1 ? "s" : ""}`}
          icon={<Users size={20} />}
          expanded={isExpanded(SECTION_IDS.membership)}
          onToggleExpanded={() => toggle(SECTION_IDS.membership)}
        >
          <div className={layout.cardInner}>
            {membersError && <Callout variant="error">{membersError}</Callout>}

            {membersLoading || (isOrgAdmin && invitesLoading) ? (
              <Text level="body-medium" style={{ color: "#6B7280" }}>
                Loading members…
              </Text>
            ) : (
                <ListView<MembershipRow>
                  listId={`org-members-${orgId}`}
                  userId={user?.id ?? "anonymous"}
                  columns={membershipColumns}
                  fetchList={fetchMembershipRows}
                  contextKey={`${memberRefreshKey}-${members.length}-${pendingInvites.length}`}
                  queryOptions={{ staleTime: Infinity }}
                  enabledViews={["stack", "table"]}
                  enableSelection
                  enableRowActions={false}
                  searchPlaceholder="Search members…"
                  emptyTitle="No members yet."
                  emptySubtitle={
                    isOrgAdmin && !isDisabledOrg
                      ? 'Use "Invite member" to add someone.'
                      : undefined
                  }
                  headerActions={
                    isOrgAdmin && org && !isDisabledOrg
                      ? [
                          {
                            id: "invite",
                            label: t("pro.orgs.inviteMember"),
                            icon: <UserPlus size={14} />,
                            onClick: () => setInviteModalOpen(true),
                          },
                        ]
                      : undefined
                  }
                  getRowLabel={(row) => row.displayName}
                  onRowClick={(row) => {
                    if (row.kind !== "invited") {
                      navigate({
                        to: "/workspace/users/$userId",
                        params: { userId: row.id },
                      });
                    }
                  }}
                  getRowPinned={(row) => row.kind === "pending"}
                  getRowCtas={(row) => {
                    if (row.kind !== "pending" || !isOrgAdmin) return undefined;
                    const member = members.find((m) => m.userId === row.id);
                    if (!member) return undefined;
                    return [{
                      id: "approve-deny",
                      text: "Membership requested",
                      buttons: [
                        { id: "approve", label: "Approve", variant: "primary" as const, disabled: memberActionLoading, onClick: () => setConfirmAction({ type: "approve", member }) },
                        { id: "deny", label: "Deny", variant: "secondary" as const, disabled: memberActionLoading, onClick: () => setConfirmAction({ type: "deny", member }) },
                      ],
                    }];
                  }}
                  getContextActions={(row) => {
                    const member = members.find((m) => m.userId === row.id);
                    return buildMemberMenuActions(row, member);
                  }}
                  renderStackCard={(row, ctx) => {
                    const member = members.find((m) => m.userId === row.id);
                    const invite = pendingInvites.find((i) => i.inviteId === row.id);
                    const footerText = invite?.createdAt
                      ? `Invited ${shortDate(invite.createdAt)}`
                      : row.createdAt
                      ? `Joined ${shortDate(row.createdAt)}`
                      : undefined;
                    return (
                      <ListViewOrgMemberCard
                        key={row.id}
                        layout="stack"
                        member={row}
                        isOrgAdmin={isOrgAdmin}
                        roleOptions={roleOptions}
                        actionLoading={memberActionLoading}
                        roleDropdownDisabled={isSoleAdminById(row.id)}
                        onApprove={() => { if (member) setConfirmAction({ type: "approve", member }); }}
                        onDeny={() => { if (member) setConfirmAction({ type: "deny", member }); }}
                        onRoleChangeRequest={(newRoleId) => { if (member) setConfirmAction({ type: "role", member, newRoleId }); }}
                        onClick={ctx.onClick}
                        footerText={footerText}
                      />
                    );
                  }}
                />
              )}
            </div>
          </CollapsibleSection>

        {canViewCertifications && (
          <CollapsibleSection
            id={SECTION_IDS.certifications}
            title={t("pro.orgs.certifications")}
            subtitle={
              certLoading
                ? undefined
                : (() => {
                    const parts: string[] = [
                      `${certMembers.length} member${certMembers.length !== 1 ? "s" : ""}`,
                    ];
                    if (certPendingInvites.length > 0) {
                      parts.push(
                        `${certPendingInvites.length} pending invite${certPendingInvites.length !== 1 ? "s" : ""}`,
                      );
                    }
                    if (certAccessRequests.length > 0) {
                      parts.push(
                        `${certAccessRequests.length} access request${certAccessRequests.length !== 1 ? "s" : ""}`,
                      );
                    }
                    return parts.join(" · ");
                  })()
            }
            icon={<GraduationCap size={20} />}
            expanded={isExpanded(SECTION_IDS.certifications)}
            onToggleExpanded={() => toggle(SECTION_IDS.certifications)}
          >
            <div className={layout.cardInner}>
              {certError && <Callout variant="error">{certError}</Callout>}

              <div className={styles.certEnrollNote}>
                <span>{t("pro.orgs.enrollRoles")}</span>
                <span className={styles.certEnrollRoles}>
                  {enrollRoles.length === 0 ? (
                    <span className={styles.membersEmpty}>{t("pro.orgs.noneConfigured")}</span>
                  ) : (
                    enrollRoles.map((role) => (
                      <span
                        key={role.id}
                        className={styles.certEnrollRoleChip}
                        style={
                          role.color
                            ? {
                                backgroundColor: `${role.color}18`,
                                color: role.color,
                              }
                            : undefined
                        }
                      >
                        {role.label?.trim() || formatRoleLabel(role.name)}
                      </span>
                    ))
                  )}
                </span>
                <span className={fieldStyles.infoTooltipWrapper}>
                  <button
                    type="button"
                    className={fieldStyles.infoButton}
                    aria-label={t("pro.orgs.aboutEnrollRoles")}
                  >
                    <Info size={14} aria-hidden />
                  </button>
                  <span className={fieldStyles.infoTooltip} role="tooltip">
                    {ENROLL_ROLES_TOOLTIP}
                  </span>
                </span>
              </div>

              {certLoading ? (
                <Text level="body-medium" style={{ color: "#6B7280" }}>
                  Loading certifications…
                </Text>
              ) : (
                <ListView<CertListRow>
                  listId={`org-certifications-${orgId}`}
                  userId={user?.id ?? "anonymous"}
                  columns={[
                    {
                      id: "name",
                      label: "Name",
                      render: (row) => (
                        <span style={{ fontWeight: 600 }}>{row.displayName}</span>
                      ),
                    },
                    {
                      id: "email",
                      label: "Email",
                      render: (row) => <span>{row.email}</span>,
                    },
                    {
                      id: "status",
                      label: "Status",
                      render: (row) => {
                        if (row.kind === "access_request") {
                          return <span>{t("pro.orgs.accessRequested")}</span>;
                        }
                        if (row.kind === "invited") {
                          return <span>{t("pro.orgs.invitePending")}</span>;
                        }
                        const modules = row.member.modules;
                        return (
                          <span>
                            {modules.length === 0
                              ? "Not enrolled"
                              : `${modules.filter((m) => m.status === "complete").length}/${modules.length} complete`}
                          </span>
                        );
                      },
                    },
                  ]}
                  fetchList={fetchCertRows}
                  contextKey={`${certRefreshKey}-${certListRows.length}`}
                  forcedFlashIds={certForcedFlashIds}
                  queryOptions={{ staleTime: Infinity }}
                  enabledViews={["stack", "deck", "table"]}
                  // Content-sized rows (overrides DeckView default 300px gridAutoRows).
                  deckCardMinHeight={0}
                  enableSelection={canManageCertifications && !isDisabledOrg}
                  searchPlaceholder="Search certification members…"
                  emptyTitle="No enroll-capable members yet."
                  emptySubtitle={
                    canManageCertifications && !isDisabledOrg
                      ? 'Use "Invite to Enroll" or approve an access request.'
                      : undefined
                  }
                  headerActions={
                    canManageCertifications && !isDisabledOrg
                      ? [
                          {
                            id: "invite-enroll",
                            label: "Invite to Enroll",
                            icon: <UserPlus size={14} />,
                            onClick: () => setInviteEnrollModalOpen(true),
                          },
                        ]
                      : undefined
                  }
                  getRowLabel={(row) => row.displayName}
                  getRowPinned={(row) =>
                    row.kind === "access_request" || row.kind === "invited"
                  }
                  onRowClick={(row) => {
                    if (row.kind === "member") {
                      navigate({
                        to: "/workspace/users/$userId",
                        params: { userId: row.id },
                      });
                    } else if (row.kind === "access_request") {
                      navigate({
                        to: "/workspace/users/$userId",
                        params: { userId: row.request.userId },
                      });
                    }
                  }}
                  getRowCtas={(row) => {
                    if (
                      row.kind !== "access_request" ||
                      !canManageCertifications ||
                      isDisabledOrg
                    ) {
                      return undefined;
                    }
                    return [
                      {
                        id: "cert-access",
                        text: "Certification access requested",
                        buttons: [
                          {
                            id: "approve",
                            label: "Approve",
                            variant: "primary" as const,
                            disabled: certActionLoading,
                            onClick: () =>
                              void handleCertAccessAction(
                                "approve",
                                row.request.userId,
                              ),
                          },
                          {
                            id: "deny",
                            label: "Deny",
                            variant: "secondary" as const,
                            disabled: certActionLoading,
                            onClick: () =>
                              void handleCertAccessAction(
                                "deny",
                                row.request.userId,
                              ),
                          },
                        ],
                      },
                    ];
                  }}
                  getContextActions={getCertContextActions}
                  renderStackCard={(row, ctx) => {
                    if (row.kind === "access_request") {
                      return (
                        <ListViewTechnicianCard
                          layout="stack"
                          technician={{
                            userId: row.request.userId,
                            name: row.displayName,
                            email: row.email,
                            avatarUrl: row.request.user?.avatarUrl,
                            modules: [],
                          }}
                          badge={{
                            label: "Access requested",
                            variant: "access_requested",
                          }}
                          emptyModulesLabel="Waiting for certification access approval"
                          onClick={ctx.onClick}
                          catalogModules={certCatalogForCards}
                        />
                      );
                    }
                    if (row.kind === "invited") {
                      return (
                        <ListViewTechnicianCard
                          layout="stack"
                          technician={{
                            userId: row.invite.inviteId,
                            name: row.email,
                            email: row.email,
                            modules: [],
                          }}
                          badge={{ label: "Invite pending", variant: "invited" }}
                          emptyModulesLabel={
                            row.invite.roleLabel
                              ? `Invited as ${row.invite.roleLabel} · not registered yet`
                              : "Invited to enroll · not registered yet"
                          }
                          onClick={ctx.onClick}
                          catalogModules={certCatalogForCards}
                        />
                      );
                    }
                    return (
                      <ListViewTechnicianCard
                        layout="stack"
                        technician={toCertTechnicianCard(row.member)}
                        onClick={ctx.onClick}
                        catalogModules={certCatalogForCards}
                      />
                    );
                  }}
                  renderCard={(row, ctx) => {
                    if (row.kind === "access_request") {
                      return (
                        <ListViewTechnicianCard
                          layout="deck"
                          technician={{
                            userId: row.request.userId,
                            name: row.displayName,
                            email: row.email,
                            avatarUrl: row.request.user?.avatarUrl,
                            modules: [],
                          }}
                          badge={{
                            label: "Access requested",
                            variant: "access_requested",
                          }}
                          emptyModulesLabel="Waiting for certification access approval"
                          onClick={ctx.onClick}
                          catalogModules={certCatalogForCards}
                        />
                      );
                    }
                    if (row.kind === "invited") {
                      return (
                        <ListViewTechnicianCard
                          layout="deck"
                          technician={{
                            userId: row.invite.inviteId,
                            name: row.email,
                            email: row.email,
                            modules: [],
                          }}
                          badge={{ label: "Invite pending", variant: "invited" }}
                          emptyModulesLabel={
                            row.invite.roleLabel
                              ? `Invited as ${row.invite.roleLabel} · not registered yet`
                              : "Invited to enroll · not registered yet"
                          }
                          onClick={ctx.onClick}
                          catalogModules={certCatalogForCards}
                        />
                      );
                    }
                    return (
                      <ListViewTechnicianCard
                        layout="deck"
                        technician={toCertTechnicianCard(row.member)}
                        onClick={ctx.onClick}
                        catalogModules={certCatalogForCards}
                      />
                    );
                  }}
                />
              )}
            </div>
          </CollapsibleSection>
        )}

          {canManageRoles && (
            <CollapsibleSection
              id={SECTION_IDS.roles}
              title={t("pro.orgs.memberRoles")}
              subtitle={`${orgRoles.length} role${orgRoles.length !== 1 ? "s" : ""}`}
              icon={<Shield size={20} />}
              expanded={isExpanded(SECTION_IDS.roles)}
              onToggleExpanded={() => toggle(SECTION_IDS.roles)}
            >
              <div className={layout.cardInner}>
                {orgRoles.length === 0 ? (
                  orgRolesLoading ? (
                    <Text level="body-medium" style={{ color: "#6B7280" }}>
                      Loading roles…
                    </Text>
                  ) : (
                    <div className={styles.centeredEmpty}>
                      <p className={styles.centeredEmptyMessage}>
                        No roles have been configured for this organization yet.
                      </p>
                      {!isDisabledOrg && (
                        <Button
                          type="button"
                          color="primary"
                          variant="outline"
                          iconLeft={<Plus size={14} />}
                          onClick={openCreateRoleModal}
                        >
                          Add role
                        </Button>
                      )}
                    </div>
                  )
                ) : (
                  <ListView<OrganizationScopedRole>
                      listId={`org-roles-${orgId}`}
                      contextKey={orgRoles
                        .map(
                          (r) =>
                            `${r.id}:${r.color ?? ""}:${r.label ?? ""}:${(r.permissions ?? []).join(",")}`,
                        )
                        .join("|")}
                      queryOptions={{ staleTime: Infinity }}
                      columns={[
                        {
                          id: "label",
                          label: "Role",
                          render: (row) => (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              <Shield
                                size={14}
                                color={row.color || "#6B7280"}
                                aria-hidden
                              />
                              {roleDisplayLabel(row)}
                              {row.systemKey ? (
                                <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                                  ({row.systemKey})
                                </span>
                              ) : null}
                            </span>
                          ),
                        },
                        {
                          id: "permissions",
                          label: "Permissions",
                          render: (row) => (
                            <span style={{ fontSize: 13 }}>
                              {(row.permissions ?? []).length} permission
                              {(row.permissions ?? []).length === 1 ? "" : "s"}
                            </span>
                          ),
                        },
                      ]}
                      fetchList={fetchRolesList}
                      enabledViews={["stack", "table"]}
                      compact
                      enableSelection={!isDisabledOrg}
                      searchPlaceholder="Search roles…"
                      emptyTitle="No roles"
                      headerActions={
                        !isDisabledOrg
                          ? [
                              {
                                id: "add",
                                label: "Add role",
                                icon: <Plus size={14} />,
                                onClick: openCreateRoleModal,
                              },
                            ]
                          : undefined
                      }
                      getContextActions={
                        isDisabledOrg ? undefined : getRoleContextActions
                      }
                      onRowClick={(row) => {
                        if (!isDisabledOrg) openEditRoleModal(row);
                      }}
                      renderStackCard={(row, ctx) => {
                        const permCount = (row.permissions ?? []).length;
                        const roleColor = row.color || "#6B7280";
                        return (
                          <ListViewCard
                            id={row.id}
                            layout="stack"
                            compact={ctx.compact}
                            imageFormat="logo"
                            image={
                              <div
                                className={
                                  ctx.compact
                                    ? listViewCardStyles.logoBoxCompact
                                    : listViewCardStyles.logoBox
                                }
                              >
                                <Shield
                                  size={ctx.compact ? 22 : 32}
                                  color={roleColor}
                                  aria-hidden
                                />
                              </div>
                            }
                            title={roleDisplayLabel(row)}
                            badges={[
                              {
                                id: "permissions",
                                label: `${permCount} permission${permCount === 1 ? "" : "s"}`,
                              },
                              ...(row.systemKey
                                ? [{ id: "system", label: row.systemKey }]
                                : []),
                            ]}
                            onClick={ctx.onClick}
                          />
                        );
                      }}
                    />
                  )}
              </div>
            </CollapsibleSection>
          )}

        {isOrgAdmin && token && (
          <CollapsibleSection
            id={SECTION_IDS.featureOverrides}
            title={t("pro.users.featureOverrides")}
            icon={<Flag size={20} />}
            expanded={isExpanded(SECTION_IDS.featureOverrides)}
            onToggleExpanded={() => toggle(SECTION_IDS.featureOverrides)}
          >
            <div className={layout.cardInner}>
              <OrganizationFeatureFlagsSection
                organizationId={orgId}
                organizationName={org.name}
                token={token}
              />
            </div>
          </CollapsibleSection>
        )}

        {canDeleteOrg() && token && (
          <CollapsibleSection
            id={SECTION_IDS.security}
            title={isDisabledOrg ? "Account status" : "Security"}
            icon={<Shield size={20} />}
            expanded={isExpanded(SECTION_IDS.security)}
            onToggleExpanded={() => toggle(SECTION_IDS.security)}
          >
            <div className={layout.cardInner}>
              <OrganizationAccountDangerZone
                orgId={orgId}
                orgName={org.name}
                accessToken={token}
                isDisabled={isDisabledOrg}
                disabledSnapshot={orgDisabledSnapshot}
                canDisable={!isDisabledOrg}
                canHardDelete={canDeleteOrg()}
                canRestore={isDisabledOrg && canDeleteOrg()}
                onOrganizationChanged={() => void reloadOrg()}
              />
            </div>
          </CollapsibleSection>
        )}
      </DetailPageLayout>

      <Modal
        open={confirmAction !== null}
        setOpen={(open) => {
          if (!open && !memberActionLoading) {
            setConfirmAction(null);
            setConfirmError(null);
          }
        }}
        minWidth="420px"
        minHeight="auto"
        maxWidth="480px"
      >
        {confirmCopy && (
          <div className={styles.confirmModalBody}>
            <h3 className={styles.confirmModalTitle}>{confirmCopy.title}</h3>
            <p className={styles.confirmModalText}>{confirmCopy.body}</p>
            {confirmError && <Callout variant="error">{confirmError}</Callout>}
            <div className={styles.confirmModalActions}>
              <Button
                color="neutral"
                variant="outline"
                size="md"
                disabled={memberActionLoading}
                onClick={() => {
                  setConfirmAction(null);
                  setConfirmError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={confirmCopy.confirmColor}
                variant="solid"
                size="md"
                loading={memberActionLoading}
                onClick={() => void handleConfirmAction()}
              >
                {confirmCopy.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {org && (
        <InviteMemberModal
          open={inviteModalOpen}
          setOpen={setInviteModalOpen}
          orgId={orgId}
          orgName={org.name}
          token={token}
          roles={orgRoles}
          rolesLoading={orgRolesLoading}
          onInvited={() => void reloadPendingInvites()}
        />
      )}
      {org && canManageCertifications && (
        <InviteToEnrollModal
          open={inviteEnrollModalOpen}
          setOpen={setInviteEnrollModalOpen}
          orgId={orgId}
          orgName={org.name}
          token={token}
          roles={orgRoles}
          rolesLoading={orgRolesLoading}
          enrollRoleFallback={enrollRoles}
          onInvited={() => {
            void reloadPendingInvites();
            void reloadCertSection();
          }}
        />
      )}
      {canManageCertifications && (
        <SendCertificationReminderModal
          open={reminderModalOpen}
          setOpen={setReminderModalOpen}
          orgId={orgId}
          token={token}
          members={reminderTargets}
          onSent={() => void reloadCertSection()}
        />
      )}
      {canManageRoles && token && (
        <RoleFormModal
          open={roleModalOpen}
          setOpen={setRoleModalOpen}
          orgId={orgId}
          accessToken={token}
          availablePermissions={availablePermissions}
          existing={editingRole}
          members={members}
          memberRoleId={
            orgRoles.find((r) => r.systemKey === "member")?.id ?? null
          }
          activeAdminCount={activeAdminCount}
          onSaved={(saved) => {
            setOrgRoles((prev) => {
              const idx = prev.findIndex((r) => r.id === saved.id);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...prev[idx], ...saved };
                return next;
              }
              return [...prev, saved];
            });
          }}
          onMemberUpdated={(userId) => {
            const memberRole = orgRoles.find((r) => r.systemKey === "member");
            if (!memberRole) return;
            setMembers((prev) =>
              prev.map((m) =>
                m.userId === userId
                  ? {
                      ...m,
                      roleId: memberRole.id,
                      roleName: memberRole.name,
                      roleLabel: memberRole.label ?? "Member",
                      roleColor: memberRole.color ?? null,
                      roleSystemKey: "member",
                    }
                  : m,
              ),
            );
            setMemberRefreshKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}
